import { useUserStore } from '@/stores/userStore'
import { ApiError } from '@/lib/api/client'
import { mapAccount, type AtlasAccount } from './accountProfile'
import { refreshAccessToken } from './tokens'
import type { AuthClient, AuthResult, Session, SimpleResult } from './authClient'

/**
 * GameCodex Accounts auth client — talks to our same-origin proxy
 * (`/api/accounts/*`), which forwards to Atlas (the identity provider) and keeps
 * the refresh token in an httpOnly cookie. The browser only ever handles the
 * short-lived access token. See Atlas/_instructions/accounts-integration.md and
 * src/server/routes/accounts.ts.
 *
 * These calls use a dedicated fetch (NOT the shared `app` client) on purpose:
 * the `app` client auto-attaches a Bearer token and retries on 401, which is
 * exactly wrong for the auth endpoints themselves.
 */
const BASE = '/api/accounts'

interface SessionResponse {
  accessToken?: string
  account?: AtlasAccount
}

async function call(
  method: string,
  path: string,
  body?: unknown,
): Promise<{ ok: boolean; status: number; data: Record<string, unknown> }> {
  let res: Response
  try {
    res = await fetch(BASE + path, {
      method,
      headers: {
        accept: 'application/json',
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
    })
  } catch {
    return { ok: false, status: 0, data: { message: 'Could not reach the gate. Check your connection and try again.' } }
  }
  const text = await res.text()
  let data: Record<string, unknown> = {}
  if (text) {
    try {
      data = JSON.parse(text) as Record<string, unknown>
    } catch {
      data = {}
    }
  }
  return { ok: res.ok, status: res.status, data }
}

/** Persist a successful session response to the store and return it. */
function commit(data: SessionResponse): Session {
  const user = mapAccount(data.account as AtlasAccount)
  const token = data.accessToken ?? ''
  useUserStore.getState().setSession(user, token)
  return { user, token }
}

function isSession(data: Record<string, unknown>): data is SessionResponse & Record<string, unknown> {
  return typeof data.accessToken === 'string' && !!data.account
}

function errorFrom(data: Record<string, unknown>, fallback: string): string {
  return typeof data.message === 'string' && data.message ? data.message : fallback
}

export const atlasAuthClient: AuthClient = {
  async signIn(email, password) {
    if (!email || !password) return { ok: false, error: 'Speak both your name and the secret word.' }
    const r = await call('POST', '/login', { email, password })
    if (r.ok && isSession(r.data)) return { ok: true, session: commit(r.data) }
    return {
      ok: false,
      error: errorFrom(r.data, r.status === 503 ? 'Sign-in is not available right now.' : 'Those credentials were not recognised.'),
    }
  },

  async signInWithProvider(provider) {
    // Social sign-in is a full-page redirect, not an in-place exchange: navigate
    // to our BFF, which 302s the browser DIRECTLY to Atlas's OAuth start (it must
    // reach Atlas first-party so the IdP state cookie lands on the host the
    // callback returns to — see routes/accounts.ts). Atlas bounces through the
    // provider and back to /auth/verify with the session in the URL fragment,
    // which AccountVerifyPage adopts. We never resolve: the page is unloading, so
    // the caller keeps its busy state rather than flashing a transient result.
    window.location.assign(`${BASE}/oauth/${provider}/start`)
    return new Promise<AuthResult>(() => {})
  },

  async signUp(input) {
    if (!input.isAdult) {
      return { ok: false, error: 'You must be 13 or older to forge a pact with Grymwyrd.' }
    }
    if (!input.email || !input.password || !input.displayName) {
      return { ok: false, error: 'A name, an email and a secret word are all required.' }
    }
    const r = await call('POST', '/register', { email: input.email, password: input.password, name: input.displayName })
    if (r.ok && isSession(r.data)) return { ok: true, session: commit(r.data) }
    return { ok: false, error: errorFrom(r.data, 'We could not create that account. The email may already be spoken for.') }
  },

  async signOut() {
    await call('POST', '/logout').catch(() => undefined)
    useUserStore.getState().clear()
  },

  async resetPassword(email) {
    if (!email) return { ok: false, error: 'Whisper the email tied to your soul.' }
    // Atlas has no password-reset endpoint; the magic link IS the recovery path
    // (passwordless sign-in). intent:'reset' rides the signed returnUrl so the
    // verify landing routes to set-password regardless of which browser opens
    // the emailed link. Always reports ok — enumeration-safe.
    await call('POST', '/request-magic-link', { email, intent: 'reset' }).catch(() => undefined)
    return { ok: true }
  },

  async getSession() {
    const { user, token } = useUserStore.getState()
    return user && token ? { user, token } : null
  },
}

// --- Standalone helpers (not on the AuthClient surface) -------------------

/**
 * Set (or reset) the signed-in account's password. The user must be signed in —
 * on the forgot-password path they get here by clicking a magic link first. Sends
 * the current access token; the BFF + Atlas enforce the real auth.
 */
export async function setAccountPassword(password: string): Promise<SimpleResult> {
  if (!password || password.length < 8) return { ok: false, error: 'Choose a password of at least 8 characters.' }
  const token = useUserStore.getState().token
  if (!token) return { ok: false, error: 'Your session has lapsed — request a new sign-in link and try again.' }
  let res: Response
  try {
    res = await fetch(`${BASE}/set-password`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ password }),
      credentials: 'same-origin',
    })
  } catch {
    return { ok: false, error: 'Could not reach the gate. Check your connection and try again.' }
  }
  if (res.ok) return { ok: true }
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>
  return { ok: false, error: errorFrom(data, 'We could not set your password. Try again.') }
}

/**
 * Ask Atlas to email a magic sign-in link. Enumeration-safe (always ok).
 * `intent` (e.g. `'reset'`) rides in the signed returnUrl Atlas emails
 * (`…/auth/verify?token=…&intent=reset`) — see AccountVerifyPage, which reads
 * it from the URL instead of a localStorage flag that a different browser/
 * profile opening the email link would never see.
 */
export async function requestMagicLink(email: string, intent?: string): Promise<SimpleResult> {
  if (!email) return { ok: false, error: 'A valid email is required.' }
  const r = await call('POST', '/request-magic-link', intent ? { email, intent } : { email })
  if (r.status === 503) return { ok: false, error: 'Sign-in is not available right now.' }
  return { ok: true }
}

/** Exchange an emailed magic-link token for a session. Throws ApiError on failure. */
export async function verifyMagicLink(token: string): Promise<Session> {
  const r = await call('GET', `/verify?token=${encodeURIComponent(token)}`)
  if (r.ok && isSession(r.data)) return commit(r.data)
  throw new ApiError(
    r.status || 400,
    (r.data.error as string) ?? 'ERROR',
    errorFrom(r.data, 'This sign-in link is invalid or has expired. Request a new one.'),
  )
}

/**
 * Adopt a session from a refresh token that arrived in the magic-link return URL
 * **fragment** (`#access=…&refresh=…`). Atlas redirects allow-listed origins
 * this way, bypassing the `/verify` proxy — so the refresh token must be handed
 * to our server (`POST /adopt`) to be exchanged + parked in the httpOnly cookie.
 * This is the durable fragment path: the returned access token is freshly minted
 * and the rotated refresh token lives in the cookie for later silent refresh.
 * Throws ApiError on failure.
 */
export async function adoptSession(refreshToken: string): Promise<Session> {
  const r = await call('POST', '/adopt', { refreshToken })
  if (r.ok && isSession(r.data)) return commit(r.data)
  throw new ApiError(
    r.status || 400,
    (r.data.error as string) ?? 'ERROR',
    errorFrom(r.data, 'This sign-in link is invalid or has expired. Request a new one.'),
  )
}

/**
 * Complete sign-in from an already-minted access token only (no refresh token in
 * the fragment). Atlas has done the exchange; we skip `/verify` and hydrate the
 * profile from `/me`. NOTE: without a refresh token this session can't be
 * silently renewed — it lasts until the access token expires — so `adoptSession`
 * is preferred whenever a refresh token is present. Throws ApiError on failure.
 */
export async function completeSessionFromAccessToken(accessToken: string): Promise<Session> {
  let res: Response
  try {
    res = await fetch(`${BASE}/me`, {
      headers: { accept: 'application/json', authorization: `Bearer ${accessToken}` },
      credentials: 'same-origin',
    })
  } catch {
    throw new ApiError(0, 'NETWORK', 'Could not reach the gate. Check your connection and try again.')
  }
  if (!res.ok) {
    throw new ApiError(res.status, 'INVALID_TOKEN', 'This sign-in link is invalid or has expired. Request a new one.')
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
  // /me may return the account directly or wrapped as { account }.
  const account = (body.account ?? body) as AtlasAccount
  if (!account?.id) {
    throw new ApiError(502, 'BAD_PROFILE', 'We signed you in but could not load your profile. Try again.')
  }
  const user = mapAccount(account)
  useUserStore.getState().setSession(user, accessToken)
  return { user, token: accessToken }
}

/**
 * Restore the session on app load. Validates the stored access token via `/me`;
 * if it's missing or expired, tries the refresh cookie. Leaves the user
 * signed-out (and clears any stale store state) if neither works.
 */
export async function bootstrapSession(): Promise<void> {
  const store = useUserStore.getState()
  const token = store.token
  if (token) {
    try {
      const res = await fetch(`${BASE}/me`, {
        headers: { accept: 'application/json', authorization: `Bearer ${token}` },
        credentials: 'same-origin',
      })
      if (res.ok) {
        const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
        // /me may return the account directly or wrapped as { account }.
        const account = (body.account ?? body) as AtlasAccount
        if (account?.id) {
          store.setSession(mapAccount(account), token)
          return
        }
      }
    } catch {
      /* fall through to refresh */
    }
  }
  const fresh = await refreshAccessToken()
  if (!fresh && token) store.clear()
}
